import { Injectable } from '@angular/core';
import { SETTINGS } from '../configs/common.settings';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';

export interface TimeClockTotalsRow {
  userId: string;
  staffName: string;
  shifts: number;
  autoClockOuts: number;
  minutes: number;
}

export interface TimeClockTotalsResponse {
  rows: TimeClockTotalsRow[];
  missingClockOuts: number;
  totalMinutes: number;
}

@Injectable({
  providedIn: 'root',
})
export class JobService {
  constructor(private http: HttpClient, private router: Router) {}

  createJob(payload: any): Observable<any> {
    const options: any = { timeZoneName: 'long' };
    const formatter = new Intl.DateTimeFormat([], options);
    payload.timezone = formatter.resolvedOptions().timeZone;
    return this.http.post(`${SETTINGS.BASE_API}/job/create`, payload);
  }

  /** CR-13 — duplicate a calendar job as an unassigned DRAFT. */
  createDraftDuplicate(payload: {
    sourceId: string;
    startDateAndTime?: Date | string;
    endDateAndTime?: Date | string;
    staff?: string[];
    isAdditional?: boolean;
  }): Observable<any> {
    return this.http.post(`${SETTINGS.BASE_API}/job/create-draft-duplicate`, payload);
  }

  /** Split one recurring occurrence into an additional DRAFT (optional cancel that occurrence). */
  splitRecurringOccurrence(payload: {
    sourceJobId: string;
    occurrenceDate: string;
    startDateAndTime: Date | string;
    endDateAndTime: Date | string;
    staff?: string[];
    cancelOccurrence?: boolean;
  }): Observable<any> {
    return this.http.post(`${SETTINGS.BASE_API}/job/split-recurring-occurrence`, payload);
  }

  /** Cancel a single recurring occurrence without cancelling the series. */
  cancelRecurringOccurrence(payload: {
    mainJob: string;
    date: string;
  }): Observable<any> {
    return this.http.post(`${SETTINGS.BASE_API}/job/recurring/cancel-occurrence`, payload);
  }

  /** CR-13 — publish DRAFT jobs to PLANNING. */
  publishDrafts(ids: string[]): Observable<{ published: string[]; errors: any[] }> {
    return this.http.post<{ published: string[]; errors: any[] }>(
      `${SETTINGS.BASE_API}/job/publish-drafts`,
      { ids }
    );
  }

  updateJob(payload: any): Observable<any> {
    const options: any = { timeZoneName: 'long' };
    const formatter = new Intl.DateTimeFormat([], options);
    payload.timezone = formatter.resolvedOptions().timeZone;
    return this.http.post(`${SETTINGS.BASE_API}/job/update`, payload);
  }

  getPagedJobs(payload: any): Observable<any> {
    return this.http.post(`${SETTINGS.BASE_API}/job/get-paged`, payload);
  }

  getPagedKanban(payload: any): Observable<any> {
    return this.http.post(`${SETTINGS.BASE_API}/job/get-paged-kanban`, payload);
  }

  getOneJobById(id: any): Observable<any> {
    return this.http.get(`${SETTINGS.BASE_API}/job/get/${id}`);
  }

  /** Contract (parent) jobs that an additional job can be attached to. */
  getContractJobOptions(filters: {
    company?: string | null;
    client?: string | null;
    search?: string;
    limit?: number;
  }): Observable<any[]> {
    return this.http.post<any[]>(`${SETTINGS.BASE_API}/job/contract-options`, filters);
  }

  /** Latest active project and contact person for a client. */
  getClientJobDefaults(clientId: string): Observable<{ project: any; contact: any }> {
    return this.http.get<{ project: any; contact: any }>(
      `${SETTINGS.BASE_API}/job/client-defaults/${clientId}`
    );
  }
  getFilteredClientById(payload: any): Observable<any> {
    return this.http.post(`${SETTINGS.BASE_API}/job/get`, payload);
  }

  getAttachmentsByJobId(id: any): Observable<any> {
    return this.http.get(`${SETTINGS.BASE_API}/job/get-attachment/${id}`);
  }

  getJobsByCompany(data: any): Observable<any> {
    return this.http.post(`${SETTINGS.BASE_API}/job/get-by-company`, data);
  }

  // Recurring Job APIs
    createRecurringModify(payload: any): Observable<any> {
        // const options: any = { timeZoneName: 'long' };
        // const formatter = new Intl.DateTimeFormat([], options);
        // payload.timezone = formatter.resolvedOptions().timeZone;
        return this.http.post(`${SETTINGS.BASE_API}/job/recurring/create/modify`, payload);
    }

    getOneRecurringModify(payload: any): Observable<any> {
        return this.http.get(
            `${SETTINGS.BASE_API}/job/recurring/get/modify?_id=${payload._id}&date=${payload.date}`
        );
    }

  getOneRecurringJobById(payload: any): Observable<any> {
    return this.http.get(
      `${SETTINGS.BASE_API}/job/recurring?_id=${payload._id}&timezone=${payload.timezone}&time=${payload.startTime}`
    );
  }

  archiveRecurringJobSeries(id: any): Observable<any> {
    return this.http.get(`${SETTINGS.BASE_API}/job/cancel-series/${id}`);
  }

  updateRecurringJob(payload: any): Observable<any> {
    return this.http.put(`${SETTINGS.BASE_API}/job/update-recurring`, payload);
  }

  getPagedRecurringJobs(payload: any): Observable<any> {
    return this.http.post(
      `${SETTINGS.BASE_API}/job/recurring/get-paged`,
      payload
    );
  }

  getPagedClientJobs(payload: any): Observable<any> {
    return this.http.post(
      `${SETTINGS.BASE_API}/job/get-paged-by-client-id`,
      payload
    );
  }

  getAllJobTitle(): Observable<any> {
    return this.http.get(`${SETTINGS.BASE_API}/job/get-job-title`);
  }

    getJobByProject(payload: any): Observable<any> {
        return this.http.post(`${SETTINGS.BASE_API}/job/get-by-project`, payload);
    }

    getAllJobs(): Observable<any> {
        return this.http.get(`${SETTINGS.BASE_API}/job/get-all`);
    }

  /** Time Clock dashboard: shifts for selected date, all users, with attendance. */
  getTimeClockShifts(payload: {
    date: string;
    /** Optional range (YYYY-MM-DD, max 31 days). When set, `date` is ignored by the API. */
    startDate?: string;
    endDate?: string;
    companyId?: string;
    projectId?: string;
    searchStaff?: string;
    /** IMP-65 — filter to one staff member. */
    userId?: string;
    status?: string;
    page?: number;
    limit?: number;
    /** IANA timezone (e.g. Australia/Sydney). If not provided, browser timezone is sent so the selected date is correct for the user's location. */
    timezone?: string;
    /** When false, exclude archived jobs. Defaults to false so archived jobs are not shown. */
    archived?: boolean;
  }): Observable<any> {
    const body: any = { date: payload.date, page: payload.page ?? 1, limit: payload.limit ?? 10 };
    if (payload.startDate) body.startDate = payload.startDate;
    if (payload.endDate) body.endDate = payload.endDate;
    if (payload.companyId && String(payload.companyId).trim()) body.companyId = String(payload.companyId).trim();
    if (payload.projectId && String(payload.projectId).trim()) body.projectId = String(payload.projectId).trim();
    if (payload.searchStaff && payload.searchStaff.trim()) body.searchStaff = payload.searchStaff.trim();
    if (payload.userId && String(payload.userId).trim()) body.userId = String(payload.userId).trim();
    if (payload.status && String(payload.status).trim()) body.status = String(payload.status).trim();
    body.timezone = payload.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (payload.archived !== undefined) body.archived = payload.archived;
    return this.http.post<any>(`${SETTINGS.BASE_API}/job/time-clock-shifts`, body);
  }

  /** Worked hours per staff member for a date range (completed shifts only; auto clock-outs included). */
  getTimeClockTotalHours(payload: {
    start: string;
    end: string;
    companyId?: string | null;
    projectId?: string | null;
  }): Observable<TimeClockTotalsResponse> {
    return this.http.post<TimeClockTotalsResponse>(`${SETTINGS.BASE_API}/job/time-clock-total-hours`, {
      ...this.rangeBody(payload),
      ...(payload.projectId ? { projectId: payload.projectId } : {}),
    });
  }

  /** Zip of one shift's before/after photos. */
  downloadShiftPhotos(userJobId: string): Observable<HttpResponse<Blob>> {
    const tz = encodeURIComponent(this.browserTimezone());
    return this.http.get(`${SETTINGS.BASE_API}/job/shift-photos-zip/${userJobId}?timezone=${tz}`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  /** Zip of a staff member's shift photos for a date range, one folder per shift. */
  downloadTimesheetPhotos(payload: {
    userId: string;
    start: string;
    end: string;
    companyId?: string | null;
  }): Observable<HttpResponse<Blob>> {
    return this.http.post(
      `${SETTINGS.BASE_API}/job/timesheet-photos-zip`,
      { ...this.rangeBody(payload), userId: payload.userId },
      { responseType: 'blob', observe: 'response' }
    );
  }

  /** Zip of all shift photos for a company and/or project, split into before/ and after/ folders. */
  downloadTimeClockPhotos(payload: {
    start: string;
    end: string;
    companyId?: string | null;
    projectId?: string | null;
  }): Observable<HttpResponse<Blob>> {
    return this.http.post(
      `${SETTINGS.BASE_API}/job/time-clock-photos-zip`,
      { ...this.rangeBody(payload), ...(payload.projectId ? { projectId: payload.projectId } : {}) },
      { responseType: 'blob', observe: 'response' }
    );
  }

  private rangeBody(payload: { start: string; end: string; companyId?: string | null }): any {
    return {
      start: payload.start,
      end: payload.end,
      timezone: this.browserTimezone(),
      ...(payload.companyId ? { companyId: payload.companyId } : {}),
    };
  }

  private browserTimezone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /** Distinct company/client display names for filter dropdown (same values as Company column in time-clock and job-approvals). */
  getCompanyDisplayNames(companyId?: string): Observable<{ data: string[] }> {
    const body: any = {};
    if (companyId && String(companyId).trim()) body.companyId = companyId;
    return this.http.post<{ data: string[] }>(`${SETTINGS.BASE_API}/job/company-display-names`, body);
  }

  /** Update clock-in/clock-out times for a time clock record. */
  updateClockTimes(payload: {
    jobId: string;
    userId: string;
    clockInDate: string;
    clockInTime?: string;
    clockEndTime?: string;
  }): Observable<any> {
    return this.http.put<any>(`${SETTINGS.BASE_API}/job/update-clock-times`, payload);
  }

  /** CR-23 — update clock-in/out GPS without changing recorded hours. */
  updateClockLocation(payload: {
    jobId: string;
    userId: string;
    clockInDate: string;
    which: 'in' | 'out';
    locationLat?: number;
    locationLong?: number;
    clockOutLocationLat?: number;
    clockOutLocationLong?: number;
  }): Observable<any> {
    return this.http.put<any>(`${SETTINGS.BASE_API}/job/update-clock-location`, payload);
  }

  /** Create an attendance record for a shift (e.g. admin adding times for Absent). */
  createAttendance(payload: {
    jobId: string;
    userId: string;
    companyId: string;
    clockInDate: string;
    clockInTime: string;
    clockEndTime?: string;
  }): Observable<any> {
    return this.http.post<any>(`${SETTINGS.BASE_API}/job/create-attendance`, payload);
  }

  // ── Job Approvals ──

  getJobApprovals(payload: {
    userId: string;
    start: string;
    end: string;
    approvalStatus?: string;
    status?: string;
    companyId?: string;
    projectId?: string;
    searchJob?: string;
    page?: number;
    limit?: number;
    /** When false, exclude archived jobs. Defaults to false so archived jobs are not shown. */
    archived?: boolean;
  }): Observable<any> {
    const body: any = {
      userId: payload.userId,
      start: payload.start,
      end: payload.end,
      page: payload.page ?? 1,
      limit: payload.limit ?? 20,
    };
    if (payload.approvalStatus && String(payload.approvalStatus).trim()) {
      body.approvalStatus = String(payload.approvalStatus).trim();
    }
    if (payload.status && String(payload.status).trim()) {
      body.status = String(payload.status).trim();
    }
    if (payload.companyId && String(payload.companyId).trim()) {
      body.companyId = String(payload.companyId).trim();
    }
    if (payload.projectId && String(payload.projectId).trim()) {
      body.projectId = String(payload.projectId).trim();
    }
    if (payload.searchJob && payload.searchJob.trim()) {
      body.searchJob = payload.searchJob.trim();
    }
    if (payload.archived !== undefined) body.archived = payload.archived;
    return this.http.post<any>(`${SETTINGS.BASE_API}/job/job-approvals`, body);
  }

  updateApprovalStatus(userJobId: string, approvalStatus: string): Observable<any> {
    return this.http.put<any>(`${SETTINGS.BASE_API}/job/approval-status`, { userJobId, approvalStatus });
  }

  updateManagerNote(userJobId: string, managerNote: string): Observable<any> {
    return this.http.put<any>(`${SETTINGS.BASE_API}/job/manager-note`, { userJobId, managerNote });
  }

  getTimesheetExport(userId: string, start: string, end: string): Observable<any> {
    return this.http.get<any>(`${SETTINGS.BASE_API}/job/timesheet-export/${userId}/${start}/${end}`);
  }

  /** Fetch an image via the backend proxy (avoids CORS for S3 URLs). Returns a Blob. */
  getImageViaProxy(imageUrl: string): Observable<Blob> {
    return this.http.get(
      `${SETTINGS.BASE_API}/files/download-photo?url=${encodeURIComponent(imageUrl)}&inline=1`,
      { responseType: 'blob' }
    );
  }

  /** Timesheet/shift audit logs (admin changes). Not linked in app menu. */
  getTimesheetAuditLogs(params: {
    page?: number;
    limit?: number;
    actionType?: string;
    doneBy?: string;
    userId?: string;
    fromDate?: string;
    toDate?: string;
  }): Observable<{ data: any[]; totalRecords: number; page: number; limit: number }> {
    const q = new URLSearchParams();
    if (params.page != null) q.set('page', String(params.page));
    if (params.limit != null) q.set('limit', String(params.limit));
    if (params.actionType?.trim()) q.set('actionType', params.actionType.trim());
    if (params.doneBy?.trim()) q.set('doneBy', params.doneBy.trim());
    if (params.userId?.trim()) q.set('userId', params.userId.trim());
    if (params.fromDate?.trim()) q.set('fromDate', params.fromDate.trim());
    if (params.toDate?.trim()) q.set('toDate', params.toDate.trim());
    const query = q.toString();
    return this.http.get<any>(`${SETTINGS.BASE_API}/job/timesheet-audit-logs${query ? '?' + query : ''}`);
  }
}
